<template>
  <div>
    <div v-if="!carbonOffsetEquivalent.paymentReference">
      <div class="container" v-if="!loading && carbonOffsetProjects.length == 0">
        <h1>We have encountered an issue fetching offset projects from our provider.</h1>
        <h5>Don't worry though, you can still offset your carbon. Visit <a href="https://www.clevel.co.uk/"
            target="_blank">www.clevel.co.uk</a> and pick your project directly.</h5>

        <hr />

        <div class="card ribbon">
          <img src="../../assets/foot-print.svg" alt="coffee cup icon">
          <h6 class="_margin-bottom-0">Your carbon Footprint is {{ carbonOffsetEquivalent.carbonFootprint | number(1) }}
            <small>tons CO<sub>2</sub>/year</small> keep note of this
          </h6>
        </div>
      </div>

      <div class="container" v-else>
        <div class="row _margin-top-40">
          <div class="col-md-6">
            <h4>Hello, {{ carbonOffsetEquivalent.firstName }} {{ carbonOffsetEquivalent.lastName }}</h4>
          </div>

          <div class="col-md-6">
            <div class="card">
              <div class="row">
                <div class="col-md-6 footprint">
                  <img src="../../assets/foot-print.svg" alt="coffee cup icon">
                  <h6 class="_margin-bottom-0">Gas Carbon Footprint</h6>
                  {{ carbonOffsetEquivalent.carbonFootprint | number(1) }} <small>tons CO<sub>2</sub>/year</small>
                </div>

                <div class="col-md-6">
                  <h6 class="_margin-bottom-0">Total Offset Cost For Gas</h6>
                  {{ carbonOffsetEquivalent.fullCarbonCost | currency(2) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <p class="_margin-top-20">Balance your CO<sub>2</sub> emissions with the world's best carbon offset projects.
          Bring carbon down to earth with forests and communities. Align with the regenerative power of Earth's
          ecosystems
        </p>

        <div class="row">
          <div class="col-md-4" v-for="item in carbonOffsetProjects" :key="item.id">
            <div class="card project-card _margin-bottom-20" :class="{ active: selectedProject == item.name }">
              <h6 class="_margin-top-0 _margin-bottom-0">{{ item.name }}</h6>
              <span class="badge">{{ item.location }}</span>

              <div class="image _margin-top-10" :style="{ 'background-image': `url(${item.imageBase64})` }"></div>

              <p class="_margin-bottom-20">
                {{ item.discription }}
              </p>

              <div class="row">
                <div class="col-xs-6">
                  <uiv-btn type="default" @click="onDetails(item)">
                    More Info
                  </uiv-btn>
                </div>

                <div class="col-xs-6 text-right" v-show="selectedProject != item.name">
                  <uiv-btn type="primary" @click="selectedProject = item.name; selectedPercentage = {}">
                    Select
                  </uiv-btn>
                </div>
              </div>

              <div class="percentages" v-show="selectedProject == item.name">
                <hr />
                <p class="_margin-top-20 _margin-bottom-10" v-if="!selectedPercentage.id">How much do you want to offset
                  by?</p>
                <div class="row">
                  <div class="col-md-3" v-for="item in percentages" :key="item.label">
                    <div class="text-center _margin-bottom-10"
                      :class="selectedPercentage.id == item.id ? 'text-primary' : ''">
                      {{ item.label }}
                    </div>
                    <radio-button v-model="selectedPercentage" :value="item">{{ item.value | currency(2)
                      }}</radio-button>
                  </div>
                </div>
              </div>

              <uiv-btn class="_margin-top-20" type="primary" block size="lg" @click="onSubmit"
                v-show="selectedPercentage.id && selectedProject == item.name">
                Offset your carbon by {{ selectedPercentage.label }}<br />
                <b>{{ selectedPercentage.value | currency(2) }}</b>
              </uiv-btn>
            </div>
          </div>
        </div>

        <uiv-modal v-model="openDetailsModal" :title="modal.name" size="md">
          <span class="badge">{{ modal.location }}</span>
          <p>{{ modal.overview }}</p>

          <div slot="footer">
            <uiv-btn @click="openDetailsModal = false">Close</uiv-btn>
          </div>
        </uiv-modal>
      </div>
    </div>

    <div class="container _margin-top-30" v-else>
      <div class="card">
        <h3>Your carbon offset journey is complete</h3>

        <p>
          If you have any queries please contact C-Level directly quoting <span class="badge">{{
            carbonOffsetEquivalent.paymentReference }}</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import RadioButton from '../../../base/components/forms/RadioButton';
import { mapState } from 'vuex';

export default {
  name: 'CarbonOffset',
  data() {
    return {
      selectedProject: null,
      selectedPercentage: {},
      openDetailsModal: false,
      modal: {},
      percentages: [
        { id: 25, label: '25%', value: 1 },
        { id: 50, label: '50%', value: 1 },
        { id: 75, label: '75%', value: 1 },
        { id: 100, label: '100%', value: 1 }
      ]
    }
  },
  async mounted() {
    if (this.carbonOffset) {
      this.$store.dispatch('page_loader/start', 0);
      await this.$store.dispatch('carbon_offset/getProjects');
      await this.$store.dispatch('carbon_offset/getCarbonEquivalent');

      this.percentages = this.percentages.map(item => ({
        ...item,
        value: this.carbonOffsetEquivalent.fullCarbonCost * (item.id / 100)
      }));

      this.$store.dispatch('page_loader/stop');
    }

    if (!this.carbonOffsetEquivalent.customerId) {
      this.$router.push({ name: 'error', params: { status: 404 } });
    }
  },
  computed: {
    ...mapState({
      loading: ({ page_loader }) => page_loader.show,
      carbonOffset: ({ app_settings, storage }) => {
        return app_settings.carbonOffset && storage.quote.switchType != 'electricityToElectricity';
      },
      carbonOffsetProjects: ({ carbon_offset }) => carbon_offset.projects,
      carbonOffsetEquivalent: ({ carbon_offset }) => carbon_offset.equivalent
    })
  },
  methods: {
    onDetails(data) {
      this.modal = data;
      this.openDetailsModal = true;
    },
    async onSubmit() {
      this.carbonOffsetEquivalent.projectSelected = this.selectedProject;
      this.carbonOffsetEquivalent.projectPercentage = this.selectedPercentage.id;

      await this.$store.dispatch('carbon_offset/selectProject', this.carbonOffsetEquivalent);
    }
  },
  components: {
    RadioButton
  }
};
</script>

<style lang="scss" scoped>
.page-title {
  font-size: 40px;
}

.ribbon {
  border-top: 5px solid $brand-colour-primary;
  position: relative;
  padding: 30px 30px 30px 70px;
  max-width: 500px;

  img {
    position: absolute;
    top: 10px;
    left: 20px;
    max-width: 30px;
  }
}

.project-card {
  min-height: 506px;

  .image {
    background-size: cover;
    height: 200px;
    width: 100%;
    border-radius: 15px;
    background-position: 50% 50%;
  }

  &.active {
    box-shadow: 0 0 0 2px $brand-colour-primary;
  }
}

.percentages {
  .row {
    margin: 0 -5px;

    .col-md-3 {
      padding: 0 5px;
    }
  }
}

.radio-button {
  height: 40px;
  font-size: 12px;
  letter-spacing: 1px;
}

.footprint {
  position: relative;
  padding: 0 15px 0 45px !important;

  img {
    position: absolute;
    max-width: 22px;
    top: 0;
    left: 13px;
  }
}
</style>
